import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  googleAuth,
  googleRegister,
  googleLogin,
} from '../../api/user/user.api';
import {
  consumeAuthReturnTo,
  getAccountBlockedInfo,
  getAuthReturnTo,
  redirectToAppIfNeeded,
  useAuth,
} from '@repo/common';

const GoogleCallbackPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) {
      navigate('/login');
      return;
    }

    const handleGoogleFlow = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const state = params.get('state');
        const redirectUri = `${window.location.origin}${new URL(process.env.REACT_APP_GOOGLE_REDIRECT_URI).pathname}`;

        const authRes = await googleAuth(code, state, redirectUri);

        if (state === 'connect') {
          await refreshUser();
          navigate(consumeAuthReturnTo('/mypage'), { replace: true });
          return;
        }

        const {
          registered,
          socialEmail,
          firstName,
          lastName,
          providerId,
          profileUrl,
        } = authRes.data;

        if (!registered) {
          const returnTo = getAuthReturnTo('/');
          const isCreator = sessionStorage.getItem('auth:isCreator') === 'true';
          const role = sessionStorage.getItem('auth:role') || 'U';
          const registrationData = JSON.parse(
            sessionStorage.getItem('auth:registrationData') || '{}',
          );

          const registerRes = await googleRegister({
            socialEmail,
            firstName,
            lastName,
            providerId,
            profileUrl,
            isCreator,
            role,
          });

          saveUserSession(registerRes.data);

          // 앱에서 넘어온 로그인이면 앱으로 돌려보낸다.
          if (await redirectToAppIfNeeded()) return;

          navigate('/register/additional-info', {
            replace: true,
            state: {
              socialUserData: {
                socialEmail,
                firstName,
                lastName,
                profileUrl,
              },
              isCreator,
              role,
              companyName: registrationData.companyName || '',
              contactName:
                registrationData.contactName || firstName || '',
              returnTo,
            },
          });
        } else {
          await googleLogin(socialEmail);
          await refreshUser();

          if (await redirectToAppIfNeeded()) return;

          navigate(consumeAuthReturnTo('/'), { replace: true });
        }
      } catch (error) {
        console.error('Google Social Flow Error:', error);
        // 차단된 계정은 로그인 화면으로 돌려보내 사유를 보여준다
        const blocked = getAccountBlockedInfo(error);
        if (blocked) {
          navigate('/login', {
            replace: true,
            state: { blocked: true, blockedReason: blocked.reason },
          });
        }
      }
    };

    const saveUserSession = (userData) => {
      if (userData.accessToken) {
        localStorage.setItem('accessToken', userData.accessToken);
      }
    };

    handleGoogleFlow();
  }, [navigate, refreshUser]);

  return (
    <div className='flex items-center justify-center h-screen'>
      <p className='typo-b16'>Processing Google Login...</p>
    </div>
  );
};

export default GoogleCallbackPage;
